import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { IProductDataV3 } from '@df/catalog/product/product';
import { ZippyEventType, subject } from '@df/legacy/ui/zippy/zippy.abstract';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { ImageModule } from '@df/module-image/image.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { NProductViewLayout, Product } from '../product.decorator';
import { ProductService } from '../product.service';
/**
 * Displays product dimensions and details
 */
@Component({
    selector: 'product-view-dimensions-and-details',
    templateUrl: './product-view-dimensions-and-details.html',
    styleUrls: ['./product-view-dimensions-and-details.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, TranslationModule, HrefComponent, ZippyModule, ImageModule, SizeClassDirective]
})
export class ProductViewDimensionsAndDetailsComponent extends UiComponent implements OnChanges {
    /**
     * Details
     */
    @Input()
    product?: Product<IProductDataV3>;
    @Input()
    icons?: NProductViewLayout.IDimensionIcon[];
    @Input()
    sections?: NProductViewLayout.IDimensionSection[];
    @Input()
    topAttributes?: string[];
    @Input()
    rightView?: boolean;
    iconsSections?: {
        image: string;
        attribute: NProductViewLayout.IAttributeItem;
    }[];
    dimensionsSections?: {
        title: string;
        attributes: NProductViewLayout.IAttributeItem[];
    }[] = [];
    displayedTopAttributes?: NProductViewLayout.IAttributeItem[];
    zippyNamePrefix = 'section-';
    constructor(
        private productService: ProductService
    ) {
        super();
    }
    override ngOnChanges(changes): void {
        if (changes.icons || changes.product || changes.sections || changes.topAttributes) {
            this.iconsSections =
                this.icons
                    ?.map(icon => {
                        const attributeCode = typeof icon.attributes === 'string' ? icon.attributes : icon.attributes?.[0];
                        if (attributeCode) {
                            const attributeItem = this.productService.getAttributeItem(attributeCode, this.product as Product);
                            if (attributeItem) {
                                return {
                                    image: icon.image,
                                    attribute: attributeItem
                                };
                            }
                        }
                        return undefined;
                    })
                    .filter(i => !!i) || [];
            const dimensionsSection =
                this.sections
                    ?.map(section => {
                        const sectionAttributes = Array.isArray(section.attributes) ? section.attributes : [section.attributes];
                        const attributesItems = sectionAttributes
                            ?.map(i => this.productService.getAttributeItem(i as string, this.product as Product))
                            .filter(i => !!i);
                        if (attributesItems?.length) {
                            return {
                                title: section.title,
                                attributes: attributesItems
                            };
                        }
                        return undefined;
                    })
                    .filter(i => !!i) || [];
            this.dimensionsSections = dimensionsSection;
            this.displayedTopAttributes = [
                {
                    isText: true,
                    isTextArea: false,
                    code: '',
                    label: '',
                    valuesLabels: [
                        `<a href="/assets/pdf/will-it-fit.pdf" target="_blank"><u>Will it fit?</u></a>`
                    ]
                },
                ...(this.topAttributes?.map(code => this.productService.getAttributeItem(code, this.product as Product))?.filter(i => !!i).slice(0, 2) || [])
            ];
            this.openDefaultTab();
        }
        super.ngOnChanges(changes);
    }
    openDefaultTab() {
        subject.next({
            id: {
                name: this.zippyNamePrefix + '0',
                group: this.zippyNamePrefix + '0',
                isGroup: false
            },
            type: ZippyEventType.open
        });
    }
    isPDF(filename: string | number): boolean {
        if (typeof filename === 'string') {
            return filename?.split('.').pop()?.toLowerCase() === 'pdf';
        }
        return false;
    }
    hasLongDescription(item): boolean {
        return item.length > 30 ? true : false;
    }
}
